import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import LoadingScreen from './components/shared/LoadingScreen';

// Admin Components
import AdminRoute from './admin/AdminRoute';
import AdminLayout from './admin/AdminLayout';
import AdminLogin from './admin/AdminLogin';
import Dashboard from './admin/Dashboard';
import BannerManager from './admin/BannerManager';
import LoanInquiries from './admin/LoanInquiries';
import MemberManager from './admin/MemberManager';
import NewsManager from './admin/NewsManager';
import GalleryManager from './admin/GalleryManager';
import ContactInquiries from './admin/ContactInquiries';
import LoanManager from './admin/LoanManager';
import DepositSchemesManager from './admin/DepositSchemesManager';
import JobManager from './admin/JobManager';
import StatsManager from './admin/StatsManager';
import BranchManager from './admin/BranchManager';

// Lazy load pages for performance
const Home = lazy(() => import('./pages/Home'));
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));
const DepositSchemes = lazy(() => import('./pages/DepositSchemes'));
const Members = lazy(() => import('./pages/Members'));
const News = lazy(() => import('./pages/News'));
const NewsDetail = lazy(() => import('./pages/NewsDetail'));
const Gallery = lazy(() => import('./pages/Gallery'));
const LoanApplication = lazy(() => import('./pages/LoanApplication'));
const Careers = lazy(() => import('./pages/Careers'));
const JobApplication = lazy(() => import('./pages/JobApplication'));
const Branches = lazy(() => import('./pages/Branches'));

// Service Pages
const GoldLoan = lazy(() => import('./pages/Services/GoldLoan'));
const MortgageLoan = lazy(() => import('./pages/Services/MortgageLoan'));
const LoanAgainstDeposit = lazy(() => import('./pages/Services/LoanAgainstDeposit'));
const BusinessLoan = lazy(() => import('./pages/Services/BusinessLoan'));
const AgricultureAlliedLoan = lazy(() => import('./pages/Services/AgricultureAlliedLoan'));
const VehicleLoan = lazy(() => import('./pages/Services/VehicleLoan'));

const AppContent = () => {
  const location = useLocation();
  const isAdminPath = location.pathname.startsWith('/admin');
  const { i18n } = useTranslation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    if (i18n.language) {
      document.documentElement.lang = i18n.language;
    }
  }, [i18n.language]);

  return (
    <div className="flex flex-col min-h-screen">
      {!isAdminPath && <Navbar />}
      <main className="flex-grow">
        <Suspense fallback={<LoadingScreen />}>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/deposit-schemes" element={<DepositSchemes />} />
            <Route path="/members" element={<Members />} />
            <Route path="/news" element={<News />} />
            <Route path="/news/:id" element={<NewsDetail />} />
            <Route path="/branches" element={<Branches />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/apply-loan" element={<LoanApplication />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/apply-job" element={<JobApplication />} />
            
            {/* Services Routes */}
            <Route path="/services/gold-loan" element={<GoldLoan />} />
            <Route path="/services/mortgage-loan" element={<MortgageLoan />} />
            <Route path="/services/loan-against-deposit" element={<LoanAgainstDeposit />} />
            <Route path="/services/business-loan" element={<BusinessLoan />} />
            <Route path="/services/agriculture-allied-loan" element={<AgricultureAlliedLoan />} />
            <Route path="/services/vehicle-loan" element={<VehicleLoan />} />

            {/* Admin Routes */}
            <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/dashboard" element={<AdminRoute><AdminLayout><Dashboard /></AdminLayout></AdminRoute>} />
            <Route path="/admin/banners" element={<AdminRoute><AdminLayout><BannerManager /></AdminLayout></AdminRoute>} />
            <Route path="/admin/stats" element={<AdminRoute><AdminLayout><StatsManager /></AdminLayout></AdminRoute>} />
            <Route path="/admin/loans" element={<AdminRoute><AdminLayout><LoanInquiries /></AdminLayout></AdminRoute>} />
            <Route path="/admin/manage-loans" element={<AdminRoute><AdminLayout><LoanManager /></AdminLayout></AdminRoute>} />
            <Route path="/admin/deposit-schemes" element={<AdminRoute><AdminLayout><DepositSchemesManager /></AdminLayout></AdminRoute>} />
            <Route path="/admin/members" element={<AdminRoute><AdminLayout><MemberManager /></AdminLayout></AdminRoute>} />
            <Route path="/admin/news" element={<AdminRoute><AdminLayout><NewsManager /></AdminLayout></AdminRoute>} />
            <Route path="/admin/jobs" element={<AdminRoute><AdminLayout><JobManager /></AdminLayout></AdminRoute>} />
            <Route path="/admin/gallery" element={<AdminRoute><AdminLayout><GalleryManager /></AdminLayout></AdminRoute>} />
            <Route path="/admin/manage-branches" element={<AdminRoute><AdminLayout><BranchManager /></AdminLayout></AdminRoute>} />
            <Route path="/admin/contacts" element={<AdminRoute><AdminLayout><ContactInquiries /></AdminLayout></AdminRoute>} />
          </Routes>
        </Suspense>
      </main>
      {!isAdminPath && <Footer />}
    </div>
  );
};

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
